import { initializeApp, type FirebaseApp } from 'firebase/app'
import {
  getAuth,
  type Auth,
  connectAuthEmulator,
  onAuthStateChanged,
  onIdTokenChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut as fbSignOut,
  RecaptchaVerifier,
  linkWithPhoneNumber,
  type ConfirmationResult,
  type User,
} from 'firebase/auth'
import { setAuthTokenProvider, setDeviceRecoveryHandler, setSessionFailureHandler } from '../api/client'
import { resetSessionCache } from './sessionCache'
import { claimDevice, clearDeviceGate, releaseDevice, reportSessionFailure, resetDeviceSession } from './deviceSession'
import { clearPersistedAppState } from './persistedState'
import { clearOrgStashes } from './inviteToken'

/**
 * Firebase web client init. Reads config from Vite env vars
 * (VITE_FIREBASE_*) — populate via .env / .env.local for dev, GitHub
 * Actions secrets for prod build.
 *
 * The init is idempotent — if it's been called once we reuse the
 * cached app + auth instances. React Strict Mode invokes effects
 * twice in dev, so this guard matters.
 */

interface FirebaseClientConfig {
  apiKey: string
  authDomain: string
  projectId: string
  appId: string
  measurementId?: string
}

let cachedApp: FirebaseApp | null = null
let cachedAuth: Auth | null = null

/**
 * Resolves on the first `onAuthStateChanged` tick — i.e. once Firebase
 * has restored (or confirmed the absence of) a persisted session. The
 * ID-token provider awaits this so the very first API call (e.g. the
 * search on initial page load) doesn't race ahead of auth init and go
 * out anonymous when the user is actually signed in. If Firebase never
 * initializes (missing config), the provider is never wired up, so
 * nothing awaits this and there's no hang.
 */
let resolveAuthReady: (() => void) | null = null
export const authReady: Promise<void> = new Promise((resolve) => {
  resolveAuthReady = resolve
})

function readConfig(): FirebaseClientConfig {
  const env = import.meta.env
  const required = ['VITE_FIREBASE_API_KEY', 'VITE_FIREBASE_AUTH_DOMAIN', 'VITE_FIREBASE_PROJECT_ID', 'VITE_FIREBASE_APP_ID'] as const
  for (const key of required) {
    if (!env[key]) throw new Error(`Missing ${key} — Firebase Auth cannot initialize`)
  }
  return {
    apiKey: env.VITE_FIREBASE_API_KEY as string,
    authDomain: env.VITE_FIREBASE_AUTH_DOMAIN as string,
    projectId: env.VITE_FIREBASE_PROJECT_ID as string,
    appId: env.VITE_FIREBASE_APP_ID as string,
    measurementId: env.VITE_FIREBASE_MEASUREMENT_ID as string | undefined,
  }
}

export function initFirebase(): { app: FirebaseApp; auth: Auth } {
  if (cachedApp && cachedAuth) return { app: cachedApp, auth: cachedAuth }

  cachedApp = initializeApp(readConfig())
  cachedAuth = getAuth(cachedApp)

  // Resolve authReady on the first auth-state tick so token-bearing
  // requests wait for session restore before firing.
  const unsubReady = onAuthStateChanged(cachedAuth, () => {
    unsubReady()
    resolveAuthReady?.()
    resolveAuthReady = null
  })

  // Hook the API client into Firebase Auth — every authed request gets
  // a fresh ID token, refreshed by the SDK as needed. Await authReady
  // first so the initial request doesn't outrun session restore and go
  // out token-less for a signed-in user.
  setAuthTokenProvider(async () => {
    await authReady
    const u = cachedAuth?.currentUser
    if (!u) return null
    return await u.getIdToken()
  })

  // A 401 / `device_evicted` from ANY api() call now has one owner instead of
  // none. Before this, the desktop shell had no auth gate at all: a session
  // revoked server-side kept rendering already-fetched prices and supplier
  // contact details until the user happened to navigate.
  setSessionFailureHandler(reportSessionFailure)
  // …and `device_not_registered` gets a repair attempt before it gets a
  // message: re-claim this device, then retry the request once. That window is
  // ordinary now — the server binds a device row to the session that claimed
  // it, so a re-authentication leaves the row stale until the claim lands.
  // `cause:'interrupted'` — this fires because a REAL request was refused, so
  // the user was already working. If it ends at the interstitial, the copy has
  // to say "your session here was replaced", not "you have just signed in".
  setDeviceRecoveryHandler(async () => (await claimDevice(undefined, { cause: 'interrupted' })) === 'ok')

  // ── DEVICE CLAIM, ON THE ONE TICK EVERY SIGN-IN PATH GOES THROUGH ───────
  // Not in signIn()/signUp(): those cover the email/password forms and miss a
  // RESTORED session, the phone-link path, and the Capacitor shell resuming a
  // persisted login. onIdTokenChanged sees all of those AND every token
  // refresh, which is what makes the key below the right one.
  //
  // KEYED ON (uid, auth_time), NOT ON uid. The server binds a device row to the
  // `auth_time` of the session that claimed it — that is what stops one browser
  // riding another's registered device id (functions/src/devices/decide.ts).
  // `auth_time` moves ONLY on a real re-authentication, and a re-authentication
  // does not change the uid, so a uid key would leave the row bound to a dead
  // session until something 403'd. A refresh keeps the same auth_time and is
  // still a no-op, so this costs no extra writes.
  let claimedKey: string | null = null
  onIdTokenChanged(cachedAuth, (user) => {
    if (!user) { claimedKey = null; clearDeviceGate(); return }
    void (async () => {
      let authTime: string | null = null
      // Cached — no network call — and a failure here must not skip the claim.
      try { authTime = (await user.getIdTokenResult()).authTime ?? null } catch { authTime = null }
      const key = `${user.uid}|${authTime ?? ''}`
      if (claimedKey === key) return
      claimedKey = key
      // Fire-and-forget: the app renders while this resolves, and claimDevice()
      // swallows everything except the two states the user must act on.
      void claimDevice()
    })()
  })

  // Local development against the Auth emulator. Toggled with
  // VITE_USE_AUTH_EMULATOR=true. Keeps prod sign-ups out of the test
  // user pool and vice-versa.
  if (import.meta.env.VITE_USE_AUTH_EMULATOR === 'true') {
    connectAuthEmulator(cachedAuth, 'http://localhost:9099', { disableWarnings: true })
  }

  return { app: cachedApp, auth: cachedAuth }
}

export function watchAuthState(cb: (user: User | null) => void): () => void {
  const { auth } = initFirebase()
  return onAuthStateChanged(auth, cb)
}

export async function signIn(email: string, password: string): Promise<User> {
  const { auth } = initFirebase()
  const cred = await signInWithEmailAndPassword(auth, email, password)
  return cred.user
}

export async function signUp(email: string, password: string): Promise<User> {
  const { auth } = initFirebase()
  const cred = await createUserWithEmailAndPassword(auth, email, password)
  return cred.user
}

export async function resetPassword(email: string): Promise<void> {
  const { auth } = initFirebase()
  await sendPasswordResetEmail(auth, email)
}

// ─── Phone verification (signup wizard OTP step) ────────────────────────────
//
// Links a phone number to the JUST-CREATED account via SMS OTP. Uses an
// invisible reCAPTCHA (Firebase requirement for phone auth on web — also fine
// inside the Capacitor shell, which loads this same site remotely). The
// verifier is created lazily against a container div the wizard renders, and
// torn down on failure so a retry gets a fresh challenge.

let recaptcha: RecaptchaVerifier | null = null

function resetRecaptcha(): void {
  try { recaptcha?.clear() } catch { /* container may already be gone */ }
  recaptcha = null
}

/**
 * Send the OTP for linking `phoneE164` (e.g. "+12125551234") to the current
 * user. Returns the confirmation to pass the 6-digit code into. Throws
 * Firebase auth errors (auth/invalid-phone-number, auth/too-many-requests,
 * auth/operation-not-allowed when SMS isn't enabled on the project…) — the
 * wizard degrades to "verify later" on any of them.
 */
export async function startPhoneLink(phoneE164: string, containerId: string): Promise<ConfirmationResult> {
  const { auth } = initFirebase()
  const user = auth.currentUser
  if (!user) throw new Error('Sign in before verifying a phone number')
  if (!recaptcha) recaptcha = new RecaptchaVerifier(auth, containerId, { size: 'invisible' })
  try {
    return await linkWithPhoneNumber(user, phoneE164, recaptcha)
  } catch (err) {
    resetRecaptcha()
    throw err
  }
}

/** Confirm the SMS code; on success the phone is linked + server-attestable. */
export async function confirmPhoneLink(confirmation: ConfirmationResult, code: string): Promise<void> {
  await confirmation.confirm(code)
  resetRecaptcha()
}

/**
 * The caller's identity for CACHE KEYS — 'anon' until a user is present.
 * Synchronous on purpose: a cache decision cannot await auth settle, and a
 * conservative 'anon' answer only ever causes a refetch, never stale data.
 * (Added 2026-09-21: the mobile browse cache served ANON-fetched locked
 * prices to a verified buyer because its entries carried no identity.)
 */
export function authCacheKey(): string {
  try {
    const { auth } = initFirebase()
    return auth.currentUser?.uid ?? 'anon'
  } catch { return 'anon' }
}

export async function signOut(): Promise<void> {
  const { auth } = initFirebase()
  // Read the uid while there still IS one — clearPersistedAppState uses it to
  // name this user's chat transcript precisely instead of wiping every scope.
  const uid = auth.currentUser?.uid ?? null
  // Hand the device slot back BEFORE the token goes away — after fbSignOut
  // there is no token to authorize the revoke with, and the slot would sit
  // occupied until the user next hit their limit. Time-boxed and best-effort
  // inside releaseDevice(): sign-out never waits on the device registry.
  await releaseDevice()
  await fbSignOut(auth)
  // Not just the gate: also the post-claim grace window, so the next person on
  // a shared workstation starts from a clean device-session slate.
  resetDeviceSession()
  // Drop session-scoped caches (favorite suppliers, financing standing) so the
  // next signed-in user never inherits the previous user's data.
  resetSessionCache()
  // …and the PERSISTED half of the same problem, which had no owner at all:
  // the Zustand store writes cart + the last 50 orders + the last 50 invoices
  // to localStorage under `aska-platform-state`, a key that is NOT scoped by
  // uid. On a shared org workstation the next person to sign in inherited all
  // of it — priced rows, rendered before any network call could correct them.
  // See lib/persistedState.ts for exactly what is and is not cleared.
  clearPersistedAppState({ uid })
  // The org stashes are the same bug in a smaller box: a pending invite holds
  // a colleague's email address and an invite token grants membership of a
  // company. Both are session-scoped already, but a tab re-used by a second
  // person on a shared workstation must not inherit either.
  clearOrgStashes()
}
